import React, { useState } from "react";
import {
  EventManagerState,
  EventSummary,
  EventFilter,
  StoredEvent,
} from "../types/eventManager";
import {
  calculateEventStats,
  filterAndSortEvents,
  getRecentEvents,
  removeEvent,
  setActiveEvent,
  exportEvent,
  importEvent,
} from "../utils/eventManager";
import {
  Trophy,
  Calendar,
  Users,
  Play,
  Pause,
  CheckCircle,
  Clock,
  BarChart3,
  Filter,
  Download,
  Upload,
  Trash2,
  Plus,
  ArrowLeft,
  Search,
  SortAsc,
  SortDesc,
  Star,
  MoreVertical,
} from "lucide-react";

interface EventManagerProps {
  eventManagerState: EventManagerState;
  setEventManagerState: (state: EventManagerState) => void;
  onSelectEvent: (event: StoredEvent | null) => void;
  onCreateNew: () => void;
  onBack: () => void;
}

const EventManager: React.FC<EventManagerProps> = ({
  eventManagerState,
  setEventManagerState,
  onSelectEvent,
  onCreateNew,
  onBack,
}) => {
  const [filter, setFilter] = useState<EventFilter>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState("");
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const stats = calculateEventStats(eventManagerState);
  const filteredEvents = filterAndSortEvents(eventManagerState, filter);
  const recentEvents = getRecentEvents(eventManagerState, 5);

  // Apply search filter
  const searchFilteredEvents = filteredEvents.filter(
    (event) =>
      event.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.eventType.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectEvent = (event: EventSummary) => {
    const storedEvent = eventManagerState.events.find((e) => e.id === event.id);
    if (storedEvent) {
      const newState = setActiveEvent(eventManagerState, event.id);
      setEventManagerState(newState);
      onSelectEvent(storedEvent);
    }
  };

  const handleDeleteEvent = (eventId: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this event? This action cannot be undone."
      )
    ) {
      const newState = removeEvent(eventManagerState, eventId);
      setEventManagerState(newState);
    }
  };

  const handleExportEvent = (eventId: string) => {
    const event = eventManagerState.events.find((e) => e.id === eventId);
    if (event) {
      const exportData = exportEvent(event);
      const blob = new Blob([exportData], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${event.name.replace(/[^a-zA-Z0-9]/g, "_")}_export.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleImportEvent = () => {
    const result = importEvent(eventManagerState, importData);
    if (result.success && result.event) {
      const newState = {
        ...eventManagerState,
        events: [...eventManagerState.events, result.event],
      };
      setEventManagerState(newState);
      setShowImportModal(false);
      setImportData("");
    } else {
      alert(`Import failed: ${result.error}`);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <Play className="w-4 h-4 text-green-600" />;
      case "completed":
        return <CheckCircle className="w-4 h-4 text-blue-600" />;
      case "paused":
        return <Pause className="w-4 h-4 text-yellow-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getEventTypeIcon = (type: string, eventType: string) => {
    if (type === "tournament") {
      return <Trophy className="w-5 h-5 text-blue-600" />;
    } else {
      return <Calendar className="w-5 h-5 text-purple-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Event Manager
                </h1>
                <p className="text-sm text-gray-600">
                  Manage all your tournaments and leagues
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </button>
              <button
                onClick={onCreateNew}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-md">
                <Trophy className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Tournaments</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.totalTournaments}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-md">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Leagues</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.totalLeagues}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-md">
                <Play className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.activeTournaments + stats.activeLeagues}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-md">
                <BarChart3 className="w-6 h-6 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">
                  Total Events
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.totalEvents}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Events */}
        {recentEvents.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Recent Events
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentEvents.map((event) => (
                <div
                  key={event.id}
                  className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-blue-500"
                  onClick={() => handleSelectEvent(event)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      {getEventTypeIcon(event.type, event.eventType)}
                      <div className="ml-3">
                        <h3 className="font-medium text-gray-900">
                          {event.name}
                        </h3>
                        <p className="text-sm text-gray-600 capitalize">
                          {event.eventType.replace("_", " ")} {event.type}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(event.status)}
                      <Star className="w-4 h-4 text-yellow-500" />
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{event.participants} teams</span>
                      <span>{event.progress}% complete</span>
                    </div>
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${event.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <select
                value={filter.type || ""}
                onChange={(e) =>
                  setFilter({ ...filter, type: e.target.value as any })
                }
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="tournament">Tournaments</option>
                <option value="league">Leagues</option>
              </select>

              <select
                value={filter.status || ""}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value as any })
                }
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="paused">Paused</option>
              </select>

              <select
                value={`${filter.sortBy || "accessed"}_${
                  filter.sortOrder || "desc"
                }`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split("_");
                  setFilter({
                    ...filter,
                    sortBy: sortBy as any,
                    sortOrder: sortOrder as any,
                  });
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="accessed_desc">Recently Accessed</option>
                <option value="created_desc">Newest First</option>
                <option value="created_asc">Oldest First</option>
                <option value="name_asc">Name A-Z</option>
                <option value="name_desc">Name Z-A</option>
                <option value="progress_desc">Most Complete</option>
                <option value="progress_asc">Least Complete</option>
              </select>
            </div>
          </div>
        </div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {searchFilteredEvents.map((event) => (
            <div
              key={event.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow border"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    {getEventTypeIcon(event.type, event.eventType)}
                    <div className="ml-3">
                      <h3 className="font-semibold text-gray-900">
                        {event.name}
                      </h3>
                      <p className="text-sm text-gray-600 capitalize">
                        {event.eventType.replace("_", " ")} {event.type}
                      </p>
                    </div>
                  </div>
                  <div className="relative">
                    <button
                      onClick={() =>
                        setSelectedEventId(
                          selectedEventId === event.id ? null : event.id
                        )
                      }
                      className="p-1 rounded-md hover:bg-gray-100"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-400" />
                    </button>
                    {selectedEventId === event.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border z-10">
                        <button
                          onClick={() => handleSelectEvent(event)}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Open Event
                        </button>
                        <button
                          onClick={() => handleExportEvent(event.id)}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Export
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(event.id)}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(event.status)}
                      <span className="text-sm text-gray-600 capitalize">
                        {event.status}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="w-4 h-4 mr-1" />
                      {event.participants} teams
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{event.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          event.status === "completed"
                            ? "bg-green-600"
                            : "bg-blue-600"
                        }`}
                        style={{ width: `${event.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500">
                    Created: {event.createdAt.toLocaleDateString()}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={() => handleSelectEvent(event)}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Open Event
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {searchFilteredEvents.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No events found
            </h3>
            <p className="text-gray-600">
              {eventManagerState.events.length === 0
                ? "Create your first tournament or league to get started."
                : "Try adjusting your search or filters."}
            </p>
          </div>
        )}
      </main>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Import Event
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Data (JSON)
                </label>
                <textarea
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Paste exported event JSON data here..."
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowImportModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleImportEvent}
                disabled={!importData.trim()}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManager;
